import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  List,
} from "@mui/material";
import IEventSession from "../../dataProvider/interfaces/IEventSession";
import { useParams } from "react-router-dom";
import { APIContext } from "../../store/APIStore";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { StoreContext } from "../../store/Store";
import { reservationClasses as classes } from "./style";
import { BiSearch, BiTrash } from "react-icons/bi";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import { debounce } from "lodash";
import { ISessionReservation } from "../../dataProvider/interfaces/IAttendee";
import SimpleActionDialog, {
  actionDialogType,
} from "../../components/SimpleActionDialog/SimpleActionDialog";
import { ManageReservationForm } from "./manageReservationForm";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import { theme } from "../../theme";
import { TiFolderOpen } from "react-icons/ti";

export interface IReservationDetail {
  sessionId: number;
  sessionAmount: number;
}
export const AssignReservations = (props: {
  id: string;
  currentReservations: ISessionReservation[];
  setCurrentAttendee: Function;
}) => {
  const [eventSessions, setEventSessions] =
    useState<IEventSession[] | null>(null);
  const [state, dispatch] = useContext(StoreContext);
  const { eventId } = useParams() as any;
  const api = useContext(APIContext);
  const notificationRef = useRef<any>(); // notification bar
  const [, setCharacters] = useState<string[]>([]);
  const { id, currentReservations, setCurrentAttendee } = props;
  const filteredRestrictedSessions = eventSessions?.filter(
    (item) => item.defaultRestrictAccessAmount !== 0
  );
  const [selectAllValue, setSelectAllValue] = useState(false);
  const debouncedSearch = useRef(
    debounce(async (criteria) => {
      setCharacters(await fetchEventSessions(criteria));
    }, 1000)
  ).current;
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    title: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [checkedItems, setCheckedItems] = useState<IReservationDetail[]>([]);
  const [unCheckedItems, setUnCheckedItems] = useState<IReservationDetail[]>(
    []
  );

  const handleItemSelect = (id: number, amount: number) => {
    const currentCheckedItems = [...checkedItems];
    const currentIndex = currentCheckedItems.findIndex(
      (item: { sessionId: number }) => item.sessionId === id
    );
    const newChecked = [...currentCheckedItems];
    if (currentIndex === -1) {
      newChecked.push({
        sessionId: id,
        sessionAmount: amount,
      });
    } else {
      const result = newChecked.splice(currentIndex, 1);
      const currentUncheckedItems = [...unCheckedItems];
      const newUncheckedItems = [...currentUncheckedItems];
      newUncheckedItems.push(result[0]);
      setUnCheckedItems(newUncheckedItems);
    }
    setCheckedItems(newChecked);
  };
  useEffect(() => {
    const checkout_all = filteredRestrictedSessions?.map((item) => item.id);
    const allItems = currentReservations.filter(item => checkout_all?.indexOf(item.eventSessionId) !== -1);
    const reservedSessions = allItems.map((item) => {
      const currentSessions = {
        sessionId: item.eventSessionId,
        sessionAmount: item.amount,
      };
      return currentSessions;
    });
    setCheckedItems(reservedSessions)
  }, [currentReservations, eventSessions])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const fetchAttendee = async () => {
    try {
      const attendee = await api.attendeeService.getAttendee(parseInt(id));
      setCurrentAttendee(attendee);
    } catch (error: any) {
      console.log(error);
      notificationRef.current?.show(
        error.response?.data.message,
        notificationType.Error
      );
    }
  };

  const manageAttendeeReservations = async () => {
    checkedItems.forEach(async (item: IReservationDetail) => {
      const data = {
        amount: item.sessionAmount,
        accessAmount: item.sessionAmount,
        message: "",
      };
      try {
        await api.attendeeService.createReservation(
          Number(id),
          eventId,
          item.sessionId,
          data
        );
        // setCheckedItems([]);
        notificationRef.current?.show(
          "Reservation successfully saved",
          notificationType.Success
        );
      } catch (error: any) {
        console.log(error.response);
        notificationRef.current?.show(
          error.response.data.message,
          notificationType.Error
        );
      }
    });
    if (unCheckedItems?.length > 0) {
      handleDeleteReservation();
    }
    setTimeout(() => {
      fetchAttendee();
    }, 3000);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (state.selectedEvent) {
      fetchEventSessions("");
    }

    return () => {
      setEventSessions(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedEvent]);

  const fetchSessionLocations = async () => {
    try {
      const locations = (await api.locationService.getLocations(
        eventId
      )) as any;
      dispatch({ type: "SET_SESSION_LOCATIONS", payload: locations });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchData = () => {
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
    fetchSessionLocations();
  };

  const fetchEventSessions = async (keyword: string) => {
    setEventSessions(null);
    try {
      let sessions = [];
      if (keyword?.length > 0) {
        sessions = (await api.sessionService.searchSession(
          eventId,
          keyword
        )) as any;
      } else {
        sessions = (await api.sessionService.getSessions(eventId)) as any;
      }
      setEventSessions(sessions);
      return sessions;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };
  async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    debouncedSearch(e.target.value);
  }
  const handleDeleteReservation = async () => {
    try {
      unCheckedItems.forEach(async (item: IReservationDetail) => {
        await api.attendeeService.removeReservation(
          Number(id),
          eventId,
          item.sessionId
        );
      });
      setUnCheckedItems([]);
      notificationRef.current?.show(
        "Session reservation has been deleted.",
        notificationType.Success
      );
      handleDialogClose();
      setTimeout(() => {
        fetchAttendee();
      }, 3000);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleAmountChange = (e: { target: { value: any } }, id: number) => {
    if (
      checkedItems.findIndex((checkedItem) => checkedItem.sessionId === id) !==
      -1
    ) {
      if (checkedItems?.length > 0) {
        const id_ = checkedItems.findIndex(
          (item: { sessionId: number }) => item.sessionId === id
        );
        checkedItems[id_].sessionAmount = e.target.value;
        setCheckedItems(checkedItems);
      } else {
        const newReserve = {
          sessionId: id,
          sessionAmount: e.target.value,
        };
        setCheckedItems([newReserve]);
      }
    }
  };
  useEffect(() => {
    if (filteredRestrictedSessions?.length === checkedItems.length) {
      setSelectAllValue(true);
    } else if (filteredRestrictedSessions?.length !== checkedItems.length) {
      setSelectAllValue(false);
    }
  }, [filteredRestrictedSessions, checkedItems]);

  const showDeleteDialog = () => {
    if (unCheckedItems.length > 0) {
      setConfirmationDialog({
        open: true,
        title: "Are you sure you want to delete this reservation?",
        icon: <BiTrash size="45" />,
        type: actionDialogType.WARNING,
        isLoading: false,
        onConfirm: () => {
          handleDeleteReservation();
        },
        onCancel: handleDialogClose,
      });
    }
  };

  const showSelectAllDialog = () => {
    if (!selectAllValue) {
      setConfirmationDialog({
        open: true,
        title: "Are you sure you want to select all the sessions",
        icon: <TiFolderOpen size="45" />,
        type: actionDialogType.WARNING,
        isLoading: false,
        onConfirm: () => {
          handleSelectAll();
        },
        onCancel: () => {
          handleDialogClose();
          if (checkedItems.length !== filteredRestrictedSessions?.length) {
            setSelectAllValue(false);
          } else {
            setSelectAllValue(true);
          }
        },
        maxWidth: "xs",
        fullWidth: true,
      });
    } else {
      setConfirmationDialog({
        open: true,
        title: "Are you sure you want to deselect all the sessions",
        icon: <TiFolderOpen size="45" />,
        type: actionDialogType.WARNING,
        isLoading: false,
        onConfirm: () => {
          handleSelectAll();
        },
        onCancel: () => {
          handleDialogClose();
          if (checkedItems.length !== filteredRestrictedSessions?.length) {
            setSelectAllValue(false);
          } else {
            setSelectAllValue(true);
          }
        },
        maxWidth: "xs",
        fullWidth: true,
      });
    }
  };
  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const handleSelectAll = () => {
    if (!selectAllValue) {
      const checkout_all = filteredRestrictedSessions?.map((item) => item.id);
      const allItems = checkedItems.filter(item => checkout_all?.indexOf(item.sessionId) !== -1);
      checkout_all?.forEach((item, i) => {
        if (
          checkedItems.findIndex(
            (checkedItem) => checkedItem.sessionId === item
          ) === -1
        ) {
          allItems.push({
            sessionId: item,
            sessionAmount:
              filteredRestrictedSessions?.[i]?.defaultRestrictAccessAmount || 1,
          });
        }
      });
      setCheckedItems(allItems);
      setConfirmationDialog({ ...confirmationDialog, open: false });
    } else {
      setCheckedItems([]);
      setSelectAllValue(false);
      const delete_all = filteredRestrictedSessions?.map((item) => item.id);
      const deleteReservesArray = [...unCheckedItems];
      delete_all?.forEach((item) => {
        deleteReservesArray.push({
          sessionId: item,
          sessionAmount: 1,
        });
      });
      setUnCheckedItems(deleteReservesArray);
      setConfirmationDialog({ ...confirmationDialog, open: false });
    }
  };

  return (
    <Box sx={classes.contentWrapper}>
      <Box sx={classes.topFilters} display="flex">
        <FdTextField
          type="search"
          onChange={handleChange}
          sx={classes.searchInput}
          autoFocus
          id="searchInput"
          placeholder="Search Sessions"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BiSearch size="18" />
              </InputAdornment>
            ),
          }}
        />
        <FdButton
          variant="contained"
          color="primary"
          sx={classes.searchButton}
          ButtonName="Search"
        />
      </Box>
      {filteredRestrictedSessions && filteredRestrictedSessions?.length > 0 ? (
        <>
          <Box aria-label="assign-resrvation-listContent">
            <Box
              sx={{
                height: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: theme.palette.grey[500],
                padding: "0 16px",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onClick={() => {
                      setSelectAllValue(!selectAllValue);
                      showSelectAllDialog();
                    }}
                    checked={selectAllValue}
                  />
                }
                label="Session"
                sx={{ "& .MuiTypography-root": { fontWeight: "bold", ml: 1 } }}
              />

              <FdTypography label="Amount" sx={{ fontWeight: "bold" }} />
            </Box>
            <Box sx={{ height: "calc(100vh - 460px)", overflow: "auto" }}>
              {eventSessions && state.sessionLocations ? (
                <List>
                  {filteredRestrictedSessions.map((item, i) => (
                    <ManageReservationForm
                      item={item}
                      isChecked={
                        checkedItems.findIndex(
                          (checkedItem) => checkedItem.sessionId === item.id
                        ) !== -1
                      }
                      handleItemSelect={handleItemSelect}
                      index={i}
                      checkedItems={checkedItems}
                      handleAmountChange={handleAmountChange}
                    />
                  ))}
                </List>
              ) : (
                <Box
                  mt={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="100%"
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Box>

          <Box mt={4}>
            <FdButton
              variant="contained"
              color="primary"
              size="small"
              type="submit"
              onClick={
                checkedItems.length > 0
                  ? manageAttendeeReservations
                  : showDeleteDialog
              }
            >
              Save
            </FdButton>
          </Box>
        </>
      ) : (
        "No Restricted sessions found"
      )}
      <NotificationMessage ref={notificationRef} />
      {confirmationDialog.open && (
        <SimpleActionDialog
          type={confirmationDialog.type}
          icon={confirmationDialog.icon}
          open={confirmationDialog.open}
          title={confirmationDialog.title}
          isLoading={confirmationDialog.isLoading}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
          onClose={handleDialogClose}
          content={confirmationDialog.content}
          confirmLabel={confirmationDialog.confirmLabel}
          maxWidth={confirmationDialog.maxWidth}
          fullWidth={confirmationDialog.fullWidth}
          cancelLabel={confirmationDialog.cancelLabel}
        />
      )}
    </Box>
  );
};
